import { Fragment } from 'react';
import { airTranslationKeys, IItineraryFareInfo, ICabinEnum } from 'obt-common';
import Box, { Flex } from '@spotnana/pixel-react/dist/Box';
import spotnanaTheme from '@spotnana/pixel-react/dist/utils/themes/theme';
import Typography from '@spotnana/pixel-react/dist/Typography';
import { useTranslation } from 'react-i18next';
import MixedCabinHeader from 'src/v2-components/flights/shared/MixedCabinHeader';

const { MIXED_CABIN_INFO_HEADING, MIXED_CABIN_INFO_MESSAGE } = airTranslationKeys;

interface MixedCabinContentProps {
  fareInfo: IItineraryFareInfo;
}

const MixedCabinContent = ({ fareInfo }: MixedCabinContentProps): JSX.Element => {
  const { t: tFlights } = useTranslation('FLIGHTS');

  return (
    <Typography variation="label" lineHeight="1.6">
      {fareInfo?.passenger?.[0]?.flights.map((flightInfo, index) => {
        const { cabin, bookingCode, flight } = flightInfo;
        const { departureAirport, arrivalAirport } = flight;
        const fareLabel = `${departureAirport} → ${arrivalAirport}`;
        const cabinName = `${tFlights(ICabinEnum[cabin])} (${bookingCode})`;
        const isLastLeg = index === fareInfo.passenger[0].flights.length - 1;

        return (
          <Fragment key={fareLabel}>
            <Box display="inline-block" whiteSpace="nowrap">
              {fareLabel}:&nbsp;{cabinName}
            </Box>
            {isLastLeg ? '' : ', '}
          </Fragment>
        );
      })}
    </Typography>
  );
};

interface MixedCabinInfoProps {
  fareInfo: IItineraryFareInfo;
}

const MixedCabinInfo = ({ fareInfo }: MixedCabinInfoProps): JSX.Element => {
  const { t: tFlights } = useTranslation('FLIGHTS');

  return (
    <Flex
      borderRadius={8}
      border="1px solid"
      borderColor={spotnanaTheme.colors.error}
      justifyContent="center"
      flexDirection="column"
      color="text.semiBold"
      width="full"
      px={32}
      py={16}
      bg={spotnanaTheme.colors.errorBackground}
      data-testid="mixed-cabin-warning-container"
    >
      <MixedCabinHeader />
      <Box mt={1} />

      <Typography variation="label" lineHeight="1.6">
        {tFlights(MIXED_CABIN_INFO_HEADING)} {tFlights(MIXED_CABIN_INFO_MESSAGE)}{' '}
      </Typography>

      <MixedCabinContent fareInfo={fareInfo} />
    </Flex>
  );
};

export default MixedCabinInfo;
